import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { z } from "zod";

import { projectSubtreeHref } from "@/features/project/lib/project-subtree-href";
import type { ProjectHierarchy, UserRead } from "@/openapi";
import { getProjectHierarchyOptions } from "@/openapi/@tanstack/react-query.gen";

export const holdingProjectSearchSchema = z.object({
  entityProjectId: z.string().optional(),
});

interface HoldingProjectLoadArgs {
  context: { user: Pick<UserRead, "organization">; queryClient: QueryClient };
  params: { id: string };
  search: z.infer<typeof holdingProjectSearchSchema>;
  location: { pathname: string; search: Record<string, unknown> };
}

/**
 * Load a holding project route's hierarchy, redirecting entity links to the
 * holding's canonical url and dropping entity ids the tree does not hold.
 */
export async function loadHoldingProject({
  context,
  params,
  search,
  location,
}: HoldingProjectLoadArgs): Promise<{ hierarchy: ProjectHierarchy }> {
  const organizationId = context.user.organization?.id;
  if (!organizationId) {
    throw redirect({ to: "/no-organization", throw: true });
  }

  const hierarchy = await context.queryClient.query({
    ...getProjectHierarchyOptions({
      path: {
        org_id: organizationId,
        project_id: params.id,
      },
    }),
    staleTime: Infinity,
  });

  // canonical url is the project root; entity links redirect to the same
  // sub-path and carry the requested node along as the active entity
  if (hierarchy.root.id !== params.id) {
    throw redirect({
      href: projectSubtreeHref(
        location,
        hierarchy.root.id,
        params.id,
        search.entityProjectId ?? params.id,
      ),
      throw: true,
    });
  }

  // heal stale or foreign entity ids in shared/bookmarked urls
  if (
    search.entityProjectId !== undefined &&
    !hierarchy.children.some((child) => child.id === search.entityProjectId)
  ) {
    throw redirect({
      href: projectSubtreeHref(
        location,
        hierarchy.root.id,
        params.id,
        undefined,
      ),
      replace: true,
      throw: true,
    });
  }

  return { hierarchy };
}
